import type { TaskListListOutput } from "@orsane/schema/task-lists";
import { BuiltInAttribute } from "@orsane/schema/tasks";
import { memo, useCallback } from "react";
import CellButton from "@/components/CellButton";
import { ListSelect } from "@/components/property-selectors/ListSelect";
import { useUpdateListsOfTaskMutation } from "@/mutations/task-mutations";
import type { ColumnDef } from "../ColumnModel";
import type { Row } from "../RowModel";
import { useBulkTargetIds } from "../useBulkTargetIds";

function getGroupingValue(row: Row): string {
    if (row.data.taskLists.length === 0) {
        return "";
    }
    // By encoding it, we take care of special chars in the name like a comma(which has its own meaning in the joining of the strings)
    return row.data.taskLists.map((x) => `${x.id}_${encodeURIComponent(x.name)}`).join(", ") ?? "None";
}

// Static functions - don't need to be recreated
const getSortValue = ({ row }: { row: Row }) => {
    return getGroupingValue(row);
};

const getGroupHeader = ({ row }: { row: Row }) => {
    const groupingValue = getGroupingValue(row);
    if (groupingValue === "") {
        return "None";
    }
    return (
        groupingValue
            .split(", ")
            .map((x) => decodeURIComponent(x.split("_")[1]))
            .join(", ") ?? "None"
    );
};

// Memoized cell component
const ListsCell = memo(function ListsCell({
    row,
    onListsChange,
}: {
    row: Row;
    onListsChange: (taskId: number, taskLists: TaskListListOutput[]) => void;
}) {
    const handleChange = useCallback(
        (taskLists: TaskListListOutput[]) => {
            onListsChange(row.data.id, taskLists);
        },
        [row.data.id, onListsChange]
    );

    return (
        <ListSelect
            trigger={(content) => (
                <CellButton disabled={row.data.isNewlyCreated}>
                    <div className="flex w-full items-center gap-1 overflow-hidden mask-linear-90 mask-linear-from-[calc(100%-20px)]">{content}</div>
                </CellButton>
            )}
            value={row.data.taskLists as TaskListListOutput[]}
            onChange={handleChange}
        />
    );
});

export default function useListsColumnBuilder() {
    const { mutate } = useUpdateListsOfTaskMutation();
    const getTargetIds = useBulkTargetIds();

    // Stable mutation handler
    const handleListsChange = useCallback(
        (taskId: number, taskLists: TaskListListOutput[]) => {
            for (const id of getTargetIds(taskId)) {
                mutate({
                    id,
                    taskLists: taskLists,
                });
            }
        },
        [mutate, getTargetIds]
    );

    return {
        build: useCallback(
            (isVisible: boolean): ColumnDef => {
                return {
                    id: BuiltInAttribute.lists,
                    header: "Lists",
                    isVisible: isVisible,
                    cssColDef: "250px",
                    isRight: false,
                    isReadOnly: false,
                    getSortValue: getSortValue,
                    getGroupHeader: getGroupHeader,
                    cell: ({ row }: { row: Row }) => <ListsCell row={row} onListsChange={handleListsChange} />,
                };
            },
            [handleListsChange]
        ),
    };
}
